import { appConfig } from '@/config/app-config';
import { csrfService } from './csrf';
import type {
  NoliaStartRequest,
  NoliaJobStatusResponse,
  NoliaJobListResponse,
  NoliaJobEvent,
  NoliaStatusResponseMapped,
  NoliaStartResponseMapped,
  GenerateUploadUrlRequest,
} from '@/types/api-types';
import {
  validateNoliaStartResponse,
  validateNoliaStatusResponse,
  validateNoliaJobStatusResponse,
  validatePresignedUrlResponse,
  mapNoliaStatusResponse,
  mapNoliaStartResponse,
} from '@/utils/api-validation';

// API Call Tracking
let apiCallCount = 0;
let stsCallCount = 0;
const sessionStartTime = Date.now();

const logApiCall = (type: 'API' | 'STS', endpoint: string) => {
  if (type === 'API') {
    apiCallCount++;
    console.warn(`📊 API CALL #${apiCallCount}: ${endpoint} (Session: ${Math.round((Date.now() - sessionStartTime) / 1000)}s)`);
  } else {
    stsCallCount++;
    console.warn(`🔐 STS CALL #${stsCallCount}: ${endpoint} (Session: ${Math.round((Date.now() - sessionStartTime) / 1000)}s)`);
  }
  console.warn(`📈 TOTAL CALLS: ${apiCallCount} API + ${stsCallCount} STS = ${apiCallCount + stsCallCount} total`);
};

/**
 * Authentication service for Numa Nolia integration
 * Handles token storage helpers and API calls to the backend proxy
 * Supports Nolia document analysis with knowledge base selection
 * Now with full type safety and runtime validation
 */

export interface User {
  email: string;
  groups: string[];
  features: string[];
  sub?: string; // User's unique identifier from Cognito
}

class AuthService {
  private readonly API_BASE_URL = ''; // Use relative URLs - nginx will proxy to backend

  async logout(): Promise<void> {
    try {
      // Call backend logout endpoint to clear cookies (with CSRF token)
      let headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      try {
        headers = await csrfService.addCSRFHeaders(headers);
      } catch {
        // CSRF fetch may fail if session is already expired — proceed anyway
      }
      await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers,
      });
    } catch (error) {
      console.warn('Logout API call failed:', error);
    }

    csrfService.clearTokens();
    console.warn('User logged out');
  }

  private resolveUploaderId(): string {
    // With secure cookie authentication, we can't access the ID token directly
    // The backend will resolve the user ID from the secure cookie
    // For now, return a default that the backend can override
    return 'nolia-user';
  }

  /**
   * Refresh the current session using cookies
   * Token refresh is now handled automatically by secure HttpOnly cookies
   */
  async refreshSession(): Promise<boolean> {
    console.warn('Attempting to refresh session via cookies...');

    try {
      const response = await fetch(`${this.API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // Include cookies for refresh
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        console.warn('Session refreshed successfully');
        return true;
      } else {
        console.error('Session refresh failed:', response.status);
        return false;
      }
    } catch (error) {
      console.error('Session refresh failed:', error);
      return false;
    }
  }

  /**
   * Build headers for XHR-based file uploads (CSRF + CloudFront secret, no Content-Type).
   * Used by knowledge-base-service.uploadFileWithProgress for authenticated FormData uploads via XHR.
   */
  async buildUploadHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
    };
    return csrfService.addCSRFHeaders(headers);
  }

  /**
   * Build request headers with CSRF token.
   * Must be called fresh for each request attempt (not reused across retries).
   */
  private async buildHeaders(options: RequestInit): Promise<Record<string, string>> {
    let headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (options.method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(options.method.toUpperCase())) {
      headers = await csrfService.addCSRFHeaders(headers);
    }

    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    return headers;
  }

  /**
   * Extract a meaningful error message from a failed response body.
   */
  private extractErrorMessage(status: number, errorText: string): string {
    let errorMessage = `API request failed: ${status}`;
    try {
      const errorData = JSON.parse(errorText);
      if (errorData.validationErrors) {
        errorMessage = errorData.validationErrors
          .map((e: { field: string; message: string }) => `${e.field}: ${e.message}`)
          .join(', ');
      } else if (errorData.error?.message) {
        errorMessage = errorData.error.message;
      } else if (typeof errorData.error === 'string') {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      if (errorText) errorMessage = errorText;
    }
    return errorMessage;
  }

  /**
   * Parse a JSON response body, tolerating an empty body (e.g. a 200/204 DELETE with no
   * content). Returns `undefined as T` rather than throwing "Unexpected end of JSON input".
   * Returns `undefined` (never `null`) so the handleCSRFRetry `!== null` sentinel still works.
   */
  private async parseResponseBody<T>(res: Response): Promise<T> {
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /**
   * Check if a 403 response is a CSRF error and retry with a fresh token.
   * Returns the retry response data if successful, or null to fall through.
   */
  private async handleCSRFRetry<T>(
    errorText: string,
    url: string,
    options: RequestInit,
  ): Promise<T | null> {
    let errorBody: { error?: string } = {};
    try { errorBody = JSON.parse(errorText); } catch { /* ignore */ }

    if (errorBody.error === 'CSRF token missing' || errorBody.error === 'Invalid CSRF token') {
      console.warn('CSRF token invalid, re-fetching and retrying...');
      csrfService.invalidate();
      const retryHeaders = await this.buildHeaders(options);
      const retryResponse = await fetch(url, {
        ...options, headers: retryHeaders, credentials: 'include',
      });
      if (retryResponse.ok) {
        return this.parseResponseBody<T>(retryResponse);
      }
      // Retry failed — throw with the retry's error
      const retryErrorText = await retryResponse.text();
      throw new Error(this.extractErrorMessage(retryResponse.status, retryErrorText));
    }

    return null; // Not a CSRF error
  }

  /**
   * Make authenticated API call using secure cookies (for STS/AWS services)
   */
  async makeSTSAuthenticatedRequest<T = unknown>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    logApiCall('STS', endpoint);
    console.warn('Making STS authenticated request:', {
      endpoint,
      method: options.method || 'GET',
      hasBody: !!options.body,
    });

    const headers = await this.buildHeaders(options);
    const url = `${this.API_BASE_URL}${endpoint}`;

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      console.warn('STS response received:', {
        status: response.status,
        ok: response.ok,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('STS API request failed:', {
          status: response.status,
          errorBody: errorText,
        });

        // 401: Auth token expired → refresh + retry with fresh headers
        if (response.status === 401) {
          console.warn('Authentication expired, attempting refresh...');
          const refreshed = await this.refreshSession();
          if (refreshed) {
            // Auth refresh may create new session → old CSRF token is stale
            csrfService.invalidate();
            const retryHeaders = await this.buildHeaders(options);
            const retryResponse = await fetch(url, {
              ...options, headers: retryHeaders, credentials: 'include',
            });
            if (retryResponse.ok) {
              return this.parseResponseBody<T>(retryResponse);
            }
          }
          console.warn('Refresh failed, authentication expired — redirecting to login');
          await this.logout();
          window.location.href = '/login';
          throw new Error('Authentication expired');
        }

        // 403: CSRF token invalid → re-fetch + retry once
        if (response.status === 403) {
          const csrfResult = await this.handleCSRFRetry<T>(errorText, url, options);
          if (csrfResult !== null) return csrfResult;
        }

        throw new Error(this.extractErrorMessage(response.status, errorText));
      }

      // Some endpoints (e.g. DELETE) return an empty body even on 200;
      // calling .json() on an empty response throws "Unexpected end of JSON input".
      const text = await response.text();
      if (!text) {
        return undefined as T;
      }
      return JSON.parse(text) as T;

    } catch (error) {
      console.error('STS request error:', error);
      throw error;
    }
  }

  /**
   * Make authenticated API call using secure cookies
   */
  async makeAuthenticatedRequest<T = unknown>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    logApiCall('API', endpoint);
    console.warn('Making authenticated request:', {
      endpoint,
      method: options.method || 'GET',
      hasBody: !!options.body,
    });

    const headers = await this.buildHeaders(options);
    const url = endpoint.startsWith('http') ? endpoint : `${this.API_BASE_URL}${endpoint}`;

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      console.warn('Response received:', {
        status: response.status,
        ok: response.ok,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API request failed:', {
          status: response.status,
          errorBody: errorText,
        });

        // 401: Auth token expired → refresh + retry with fresh headers
        if (response.status === 401) {
          console.warn('Authentication expired, attempting refresh...');
          const refreshed = await this.refreshSession();
          if (refreshed) {
            // Auth refresh may create new session → old CSRF token is stale
            csrfService.invalidate();
            const retryHeaders = await this.buildHeaders(options);
            const retryResponse = await fetch(url, {
              ...options, headers: retryHeaders, credentials: 'include',
            });
            if (retryResponse.ok) {
              return this.parseResponseBody<T>(retryResponse);
            }
          }
          console.warn('Refresh failed or expired, logging out — redirecting to login');
          await this.logout();
          window.location.href = '/login';
          throw new Error('Authentication expired');
        }

        // 403: CSRF token invalid → re-fetch + retry once
        if (response.status === 403) {
          const csrfResult = await this.handleCSRFRetry<T>(errorText, url, options);
          if (csrfResult !== null) return csrfResult;
        }

        throw new Error(this.extractErrorMessage(response.status, errorText));
      }

      // Some endpoints (e.g. DELETE) return an empty body even on 200;
      // calling .json() on an empty response throws "Unexpected end of JSON input".
      const text = await response.text();
      if (!text) {
        return undefined as T;
      }
      return JSON.parse(text) as T;

    } catch (error) {
      console.error('Request error:', error);
      throw error;
    }
  }

  /**
   * Upload file to S3 using presigned URLs (NUMA pattern)
   * Now with proper type safety and validation
   */
  async uploadFileToNumaS3(file: File): Promise<string> {
    console.warn('Uploading file to S3 using presigned URL:', file.name);

    try {
      const uploaderId = this.resolveUploaderId();

      // Step 1: Get presigned URL from backend proxy with proper typing
      console.warn('Getting presigned URL from backend...');
      const requestData: GenerateUploadUrlRequest = {
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        fileSize: file.size,
        uploaderId,
      };

      const rawResponse = await this.makeSTSAuthenticatedRequest('/files/upload-url', {
        method: 'POST',
        body: JSON.stringify(requestData),
      });

      const presignedData = validatePresignedUrlResponse(rawResponse);
      const { uploadUrl, s3Key } = presignedData;
      console.warn('Got presigned URL, uploading to S3...', { uploadUrl: uploadUrl.substring(0, 100) + '...', s3Key });

      // Step 2: Upload file directly to S3 using presigned PUT
      console.warn('Uploading file to S3...', { fileName: file.name, fileType: file.type, fileSize: file.size });

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text().catch(() => '');
        throw new Error(`S3 upload failed: ${uploadResponse.status} - ${errorText || uploadResponse.statusText}`);
      }

      console.warn('File uploaded successfully to S3:', s3Key);
      return s3Key;
    } catch (error) {
      console.error('File upload failed:', error);
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Upload failed: ${message}`);
    }
  }

  /**
   * Upload files for Nolia assessment
   */
  async uploadNoliaFiles(files: File[]): Promise<{
    success: boolean;
    primaryDocumentS3Key: string;
    uploadedFiles: Array<{ name: string; s3Key: string }>;
  }> {
    console.warn('Starting file upload process for Nolia...');

    if (files.length === 0) {
      throw new Error('No files provided for upload');
    }

    // For now, just upload the first file (primary document)
    const primaryFile = files[0];
    console.warn('Uploading primary document:', primaryFile.name);

    const s3Key = await this.uploadFileToNumaS3(primaryFile);

    return {
      success: true,
      primaryDocumentS3Key: s3Key,
      uploadedFiles: [{ name: primaryFile.name, s3Key }],
    };
  }

  /**
   * Start a new Nolia analysis with proper type safety and validation
   * Expects jobId and dual KB selection (global + procurement)
   */
  async startNoliaAnalysis(payload: Omit<NoliaStartRequest, 'user_timezone'>): Promise<NoliaStartResponseMapped> {
    console.warn('Starting Nolia analysis...', { jobId: payload.jobId });

    const requestData: NoliaStartRequest = {
      ...payload,
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    const rawResponse = await this.makeSTSAuthenticatedRequest('/api/nolia/main', {
      method: 'POST',
      headers: {
      },
      body: JSON.stringify(requestData),
    });

    const validatedResponse = validateNoliaStartResponse(rawResponse);
    return mapNoliaStartResponse(validatedResponse);
  }

  /**
   * Get status of Nolia analysis with proper type safety and validation (legacy)
   */
  async getNoliaStatus(): Promise<NoliaStatusResponseMapped> {
    console.warn('Getting Nolia status...');

    const rawResponse = await this.makeSTSAuthenticatedRequest('/api/nolia/status', {
      method: 'GET',
      headers: {
      },
    });

    const validatedResponse = validateNoliaStatusResponse(rawResponse);
    return mapNoliaStatusResponse(validatedResponse);
  }

  /**
   * Get status of a specific Nolia job by ID
   * Uses the DynamoDB-backed jobs API for accurate status tracking
   */
  async getNoliaJobStatus(jobId: string): Promise<NoliaJobStatusResponse> {
    console.warn('Getting Nolia job status...', { jobId });

    const rawResponse = await this.makeSTSAuthenticatedRequest(`/api/nolia/jobs/${jobId}`, {
      method: 'GET',
      headers: {
      },
    });

    return validateNoliaJobStatusResponse(rawResponse);
  }

  /**
   * List all Nolia jobs for the authenticated user
   * Returns paginated list of jobs with status, inputs, and timing information
   */
  async listNoliaJobs(options?: {
    limit?: number;
    nextToken?: string;
  }): Promise<NoliaJobListResponse> {
    console.warn('Listing Nolia jobs...', options);

    // Build query params
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.nextToken) params.append('nextToken', options.nextToken);

    const queryString = params.toString();
    const url = `/api/nolia/jobs${queryString ? `?${queryString}` : ''}`;

    const rawResponse = await this.makeSTSAuthenticatedRequest(url, {
      method: 'GET',
      headers: {
      },
    });

    // The backend already validates this response, so we can trust it
    // But we do basic type checking here for safety
    if (!rawResponse || typeof rawResponse !== 'object') {
      throw new Error('Invalid job list response');
    }

    return rawResponse as NoliaJobListResponse;
  }

  /**
   * Poll for Nolia analysis status with proper type safety (legacy - no job ID)
   */
  async pollNoliaStatus(): Promise<NoliaStatusResponseMapped> {
    console.warn('Polling Nolia analysis status...');

    const checkStatus = async (): Promise<NoliaStatusResponseMapped | null> => {
      const result = await this.getNoliaStatus();

      if (result.status === 'SUCCESS') {
        return result;
      } else if (result.status === 'FAILURE' || result.status === 'FAILED') {
        throw new Error(result.error || 'Analysis failed');
      }
      return null;
    };

    // Poll every 5 seconds for up to 15 minutes (Nolia can take longer)
    const maxAttempts = 180;
    for (let i = 0; i < maxAttempts; i++) {
      const result = await checkStatus();
      if (result) return result;
      await new Promise(resolve => setTimeout(resolve, 5000));
    }
    throw new Error('Analysis timed out');
  }

  /**
   * Poll for Nolia job status by job ID
   * Uses the DynamoDB-backed jobs API with event streaming
   */
  async pollNoliaJobStatus(
    jobId: string,
    options?: {
      pollInterval?: number;
      maxPollingTime?: number;
      onProgress?: (_events: NoliaJobEvent[]) => void;
    },
  ): Promise<NoliaJobStatusResponse> {
    const pollInterval = options?.pollInterval ?? 5000; // 5 seconds
    const maxPollingTime = options?.maxPollingTime ?? 1800000; // 30 minutes
    const onProgress = options?.onProgress;

    console.warn('Polling Nolia job status...', { jobId, pollInterval, maxPollingTime });

    const startTime = Date.now();
    let lastEventCount = 0;

    while (true) {
      const job = await this.getNoliaJobStatus(jobId);

      // Report progress events if callback provided
      if (onProgress && job.events && job.events.length > lastEventCount) {
        onProgress(job.events);
        lastEventCount = job.events.length;
      }

      // Check for completion
      if (job.status === 'SUCCESS') {
        console.warn('Nolia job completed successfully', { jobId });
        return job;
      }

      if (job.status === 'FAILURE') {
        console.error('Nolia job failed', { jobId, error: job.error });
        throw new Error(job.error || 'Analysis failed');
      }

      // Check timeout
      if (Date.now() - startTime > maxPollingTime) {
        console.error('Nolia job polling timeout', { jobId, elapsed: Date.now() - startTime });
        throw new Error('Analysis timed out');
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
  }

  /**
   * Get the outputs bucket name from session config
   */
  getOutputsBucket(): string {
    return appConfig.OUTPUTS_BUCKET_NAME || '';
  }

  /**
   * Get the AWS region from session config
   */
  getRegion(): string {
    return appConfig.REGION || 'us-east-1';
  }

  /**
   * Get the data bucket name from session config
   */
  getDataBucket(): string {
    return appConfig.DATA_BUCKET || '';
  }

  /**
   * Complete Nolia workflow: Upload files and run assessment with proper type safety
   * Now uses job ID based polling for accurate status tracking
   */
  async runCompleteNoliaAssessment(
    files: File[],
    globalKB: string,
    procurementKB: string,
  ): Promise<{
    uploadResult: {
      success: boolean;
      primaryDocumentS3Key: string;
      uploadedFiles: Array<{ name: string; s3Key: string }>;
    };
    assessmentResult: NoliaJobStatusResponse;
    success: boolean;
  }> {
    console.warn('Starting complete Nolia assessment workflow...');

    // Step 1: Upload files to S3
    console.warn('Step 1: Uploading files to NUMA S3...');
    const uploadResult = await this.uploadNoliaFiles(files);

    // Step 2: Prepare payload for Nolia with proper typing
    // KB values are passed with "kb-" prefix to match S3 folder structure (documents/kb-{id}/)
    const primaryFile = files[0];
    const payload: Omit<NoliaStartRequest, 'user_timezone'> = {
      uploaded_file: [{
        id: uploadResult.primaryDocumentS3Key,
        name: primaryFile.name,
        s3_key: uploadResult.primaryDocumentS3Key,
      }],
      global_kb: `kb-${globalKB}`,
      procurement_kb: `kb-${procurementKB}`,
    };

    // Step 3: Start Nolia analysis - backend generates jobId
    console.warn('Step 2: Starting Nolia analysis...');
    const startResult = await this.startNoliaAnalysis(payload);
    const jobId = startResult.jobId;
    console.warn('Step 3: Received job ID from server:', jobId);

    if (!jobId) {
      throw new Error('No job ID returned from server');
    }

    // Step 4: Poll for results using job ID
    console.warn('Step 4: Polling for analysis results...');
    const assessmentResult = await this.pollNoliaJobStatus(jobId);

    return {
      uploadResult,
      assessmentResult,
      success: true,
    };
  }

}

// Export singleton instance
export const authService = new AuthService();
